/**
 * Form controls.
 *
 * Grouped in one file because they are variations on the same thing: a label, a
 * control, and an error message under it. Keeping them together makes the
 * spacing consistent, which is what makes a form look finished.
 */

import { forwardRef } from 'react';

/** Label + control + error wrapper. */
export const Field = ({ label, error, hint, required, children, className = '' }) => (
    <div className={className}>
        {label && (
            <label className="label">
                {label}
                {required && <span className="ml-0.5 text-red-500">*</span>}
            </label>
        )}
        {children}
        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        {!error && hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
);

export const Input = forwardRef(({ error, className = '', ...props }, ref) => (
    <input
        ref={ref}
        className={`input ${error ? 'border-red-400 focus:border-red-500 focus:ring-red-500' : ''} ${className}`}
        {...props}
    />
));
Input.displayName = 'Input';

/**
 * A numeric input for money and quantities.
 *
 * `inputMode="decimal"` brings up the number pad on a touchscreen, and the
 * right-aligned tabular figures make a column of amounts line up.
 */
export const NumberInput = forwardRef(({ error, className = '', ...props }, ref) => (
    <input
        ref={ref}
        type="number"
        inputMode="decimal"
        step="0.01"
        className={`input tabular text-right ${error ? 'border-red-400' : ''} ${className}`}
        // Scrolling over a focused number input silently changes it, which on a
        // bill is a real error. Blurring on wheel prevents that.
        onWheel={(event) => event.currentTarget.blur()}
        {...props}
    />
));
NumberInput.displayName = 'NumberInput';

export const Select = forwardRef(({ error, children, className = '', ...props }, ref) => (
    <select
        ref={ref}
        className={`input appearance-none bg-[length:16px] bg-[right_0.75rem_center] bg-no-repeat pr-9
                    ${error ? 'border-red-400' : ''} ${className}`}
        style={{
            backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E\")"
        }}
        {...props}
    >
        {children}
    </select>
));
Select.displayName = 'Select';

export const Textarea = forwardRef(({ error, className = '', rows = 3, ...props }, ref) => (
    <textarea
        ref={ref}
        rows={rows}
        className={`input resize-y ${error ? 'border-red-400' : ''} ${className}`}
        {...props}
    />
));
Textarea.displayName = 'Textarea';

/** Checkbox with its label as one tap target. */
export const Checkbox = ({ label, description, checked, onChange, disabled, className = '' }) => (
    <label className={`flex cursor-pointer items-start gap-3 ${disabled ? 'opacity-50' : ''} ${className}`}>
        <input
            type="checkbox"
            checked={!!checked}
            onChange={(event) => onChange?.(event.target.checked)}
            disabled={disabled}
            className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
        />
        <span className="min-w-0">
            <span className="block text-sm font-medium text-slate-700">{label}</span>
            {description && <span className="mt-0.5 block text-xs text-slate-500">{description}</span>}
        </span>
    </label>
);

/**
 * A switch, for settings that take effect immediately.
 * Visually distinct from a checkbox so "this applies now" reads differently
 * from "this applies when you save".
 */
export const Toggle = ({ label, description, checked, onChange, disabled }) => (
    <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
            <p className="text-sm font-medium text-slate-700">{label}</p>
            {description && <p className="mt-0.5 text-xs text-slate-500">{description}</p>}
        </div>
        <button
            type="button"
            role="switch"
            aria-checked={!!checked}
            disabled={disabled}
            onClick={() => onChange?.(!checked)}
            className={`relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition-colors
                        ${checked ? 'bg-brand-600' : 'bg-slate-300'}
                        ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
        >
            <span
                className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform
                            ${checked ? 'translate-x-[22px]' : 'translate-x-0.5'}`}
            />
        </button>
    </div>
);

export default Field;
